import type { NightStepId, RoleId, RoleResources, SeerAlignment, Team } from '../types';

/**
 * Deklarative Rollendefinition. Fähigkeiten selbst liegen in `actions/handlers.ts`
 * (Nachtaktionen) und `effects/deaths.ts` (Todesauslöser) – so können neue Rollen
 * ergänzt werden, ohne verstreute if-role-Abfragen anzufassen.
 */
export interface RoleDefinition {
  id: RoleId;
  name: string;
  team: Team;
  /** Ergebnis bei Untersuchung durch die Seherin. */
  seerResult: SeerAlignment;
  /** Maximale Anzahl in einer Komposition. */
  maxCount: number;
  /** Nachtphase, in der die Rolle aufgerufen wird. */
  nightStep: NightStepId | null;
  /** Darf vom Trunkenbold verdeckt werden. */
  drunkEligible: boolean;
  description: string;
  winCondition: string;
  initialResources(): RoleResources;
}

const noResources = (): RoleResources => ({});

const VILLAGE_WIN = 'Du gewinnst mit dem Dorf, sobald kein lebender Vampir mehr existiert.';

export const ROLE_DEFINITIONS: Record<RoleId, RoleDefinition> = {
  VILLAGER: {
    id: 'VILLAGER',
    name: 'Dorfbewohner',
    team: 'VILLAGE',
    seerResult: 'GOOD',
    maxCount: Number.POSITIVE_INFINITY,
    nightStep: null,
    drunkEligible: true,
    description:
      'Du hast keine Nachtaktion. Beobachte, diskutiere und stimme klug ab, um die Vampire zu entlarven.',
    winCondition: VILLAGE_WIN,
    initialResources: noResources,
  },
  VAMPIRE: {
    id: 'VAMPIRE',
    name: 'Vampir',
    team: 'VAMPIRE',
    seerResult: 'EVIL',
    maxCount: 2,
    nightStep: 'VAMPIRE_PHASE',
    drunkEligible: true,
    description:
      'Jede Nacht wählst du ein Opfer. Seid ihr zu zweit, müsst ihr euch beide auf dasselbe Ziel einigen. Einen bekannten Mitvampir dürft ihr nicht angreifen.',
    winCondition: 'Ihr gewinnt, sobald mindestens so viele Vampire wie Nicht-Vampire leben.',
    initialResources: noResources,
  },
  SEER: {
    id: 'SEER',
    name: 'Seherin',
    team: 'VILLAGE',
    seerResult: 'GOOD',
    maxCount: 1,
    nightStep: 'SEER_PHASE',
    drunkEligible: true,
    description:
      'Jede Nacht darfst du einen lebenden Spieler untersuchen. Nach Bestätigung durch den Spielleiter erfährst du, ob er Gut oder Böse erscheint. „Böse“ heißt nicht automatisch Vampir.',
    winCondition: VILLAGE_WIN,
    initialResources: noResources,
  },
  CURSED: {
    id: 'CURSED',
    name: 'Verfluchter',
    team: 'VILLAGE',
    seerResult: 'EVIL',
    maxCount: 1,
    nightStep: null,
    drunkEligible: true,
    description:
      'Du gehörst zum Dorf und hast keine Verbindung zu den Vampiren – doch die Seherin sieht dich als Böse.',
    winCondition: VILLAGE_WIN,
    initialResources: noResources,
  },
  WITCH: {
    id: 'WITCH',
    name: 'Hexe',
    team: 'VILLAGE',
    seerResult: 'GOOD',
    maxCount: 1,
    nightStep: 'WITCH_PHASE',
    drunkEligible: true,
    description:
      'Du besitzt einmalig einen Heiltrank und einen Gifttrank. Solange du den Heiltrank hast, siehst du das Vampiropfer der Nacht und darfst es heilen – auch dich selbst. Das Gift darfst du auf einen anderen lebenden Spieler anwenden, niemals auf dich.',
    winCondition: VILLAGE_WIN,
    initialResources: () => ({ healPotion: true, poisonPotion: true }),
  },
  PRIEST: {
    id: 'PRIEST',
    name: 'Priester',
    team: 'VILLAGE',
    seerResult: 'GOOD',
    maxCount: 1,
    nightStep: 'PRIEST_PHASE',
    drunkEligible: true,
    description:
      'Du erwachst als Letzter und musst jede Nacht einen Spieler schützen – auch dich selbst. Dein Schutz blockiert alle nächtlichen Todeswirkungen. Dieselbe Person darfst du nicht zwei Nächte hintereinander schützen.',
    winCondition: VILLAGE_WIN,
    initialResources: () => ({ lastProtectedId: null, lastProtectedNight: null }),
  },
  VAMPIRE_HUNTRESS: {
    id: 'VAMPIRE_HUNTRESS',
    name: 'Vampirjägerin',
    team: 'VILLAGE',
    seerResult: 'GOOD',
    maxCount: 1,
    nightStep: null,
    drunkEligible: true,
    description:
      'Wenn du stirbt – egal wodurch –, musst du einen weiteren lebenden Spieler mit in den Tod reißen.',
    winCondition: VILLAGE_WIN,
    initialResources: noResources,
  },
  FOOL: {
    id: 'FOOL',
    name: 'Narr',
    team: 'SOLO',
    seerResult: 'EVIL',
    maxCount: 1,
    nightStep: null,
    drunkEligible: false,
    description:
      'Wirst du bei der ersten Tagesabstimmung herausgewählt, gewinnst du die gesamte Partie. Überlebst du sie, wirst du ab Runde 2 ein normaler Dorfbewohner.',
    winCondition:
      'Du gewinnst allein, wenn du bei der ersten Tagesabstimmung herausgewählt wirst. Danach gewinnst du mit dem Dorf.',
    initialResources: noResources,
  },
  DEMON: {
    id: 'DEMON',
    name: 'Dämon',
    team: 'SOLO',
    seerResult: 'EVIL',
    maxCount: 1,
    nightStep: 'DEMON_PHASE',
    drunkEligible: false,
    description:
      'Jede Nacht musst du eine lebende Person besessen machen. Wen du schon hast, kannst du nicht erneut wählen, dich selbst nie. Der Schutz des Priesters wehrt dich ab.',
    winCondition:
      'Du gewinnst allein, sobald am Morgen alle lebenden Personen außer dir besessen sind.',
    initialResources: noResources,
  },
};

export function roleDef(id: RoleId): RoleDefinition {
  return ROLE_DEFINITIONS[id];
}

export const DRUNK_DISPLAY = {
  name: 'Trunkenbold',
  description:
    'Du bist zu betrunken, um zu wissen, wer du wirklich bist. Zu Beginn der dritten Nacht wirst du nüchtern und erfährst deine wahre Rolle. Bis dahin besitzt du keine Fähigkeit.',
  winCondition: 'Deine Siegbedingung erfährst du mit deiner wahren Rolle in Nacht 3.',
} as const;

export const LOVERS_WIN_TEXT =
  'Als Geliebte gewinnt ihr gemeinsam, sobald mindestens so viele Geliebte wie Nicht-Geliebte leben. Stirbt einer von euch, stirbt auch der andere.';

/** Einzige Information, die die Seherin erhält – niemals die Rolle selbst. */
export const SEER_RESULT_TEXT = {
  GOOD: 'Macht keinen verdächtigen Eindruck',
  EVIL: 'Macht einen verdächtigen Eindruck',
} as const;

/** Öffentliche Beschreibung des Geistes – u. a. für Hilfe und Twitch-Chat. */
export const GHOST_DISPLAY = {
  name: 'Geist',
  description:
    'Der Geist ist keine eigene Hauptrolle. Stirbt eine Person und der Geist ist aktiviert, kann sie zum Geist werden. Frühestens ab Nacht 2 erwacht der Geist als Letzter und hinterlässt pro Nacht genau einen Buchstaben als Hinweis für das Dorf.',
  winCondition:
    'Der Geist behält seine ursprüngliche Rolle und damit auch deren Siegbedingung.',
} as const;
